/**
 * Shared constants for the Section Leader "Practice Teaching" exercise (the
 * simulated *section* teaching flow). The applicant walks a simulated student
 * through this week's section problem and its solution.
 *
 * This is the section analogue of the 1:1 `simteaching` flow. It reuses the
 * SimTeach IDE shell and the screen recorder, but the applicant teaches a
 * problem + solution rather than debugging one student's code.
 */

import type {CourseId} from 'types/common';

/**
 * Resolves the application course that owns an attempt and its recording.
 *
 * @param applicationCourseId - Target course from the `apply` query parameter.
 * @param routeCourseId - Course in the current route, used when no target is set.
 * @returns The course id used for attempt documents and recording paths.
 * @throws Never.
 */
export function resolveSectionTeachingTargetCourseId({
  applicationCourseId,
  routeCourseId,
}: {
  applicationCourseId: string | null;
  routeCourseId: CourseId;
}): CourseId {
  return applicationCourseId || routeCourseId;
}

/**
 * Scenario the section-teaching exercise runs on. Must match a `ScenarioId`
 * in `course/simteach/scenarioConfig.ts`, whose `problem_name` is the seeded
 * assignment slug the IDE opens.
 */
export const SECTION_TEACH_SCENARIO_ID = 'guessmyanimal-teach';

/**
 * Scenario the "Practice Demo" runs on. Same section engine, a different
 * problem (AI Haiku), so the exercise can be rehearsed and recorded without
 * touching a real graded attempt. Must match a `ScenarioId` in
 * `course/simteach/scenarioConfig.ts`, whose `problem_name` is the seeded
 * assignment slug the IDE opens (`assns/public/assignments/ai-haiku`).
 */
export const DEMO_SECTION_TEACH_SCENARIO_ID = 'ai-haiku';

/**
 * Scenario the CIPX slides-based "Practice Teaching" runs on (issue #3545) —
 * what the application checklist's Practice Teaching item launches (see
 * `getTodoItemRoute`). Must match a `ScenarioId` in
 * `course/simteach/scenarioConfig.ts`; that scenario's `surface.deckId` is
 * the real applicant deck (`guess-my-animal`). Unlike
 * {@link SECTION_TEACH_SCENARIO_ID}, no IDE and no assignment: the shared
 * screen is the slide deck.
 */
export const CIPX_SECTION_TEACH_SCENARIO_ID = 'cipx-section-slides';

/**
 * Deck the staff demo-teach flavor presents (issue #4109) — the deck used to
 * record the sim-section walkthrough, pinned via
 * `SlidesSectionFlavor.deckIdOverride` so the demo never presents the real
 * applicant deck. The deck auto-creates on first slides-editor open
 * (`/{courseId}/slides/edit/{id}`); `mise seed:cipx:demo-decks-prod` mirrors
 * the prod-authored copy locally (see the functions package's
 * `seed-cipx-demo-decks-from-prod.ts`, whose `DECK_IDS` must include this id).
 */
export const CIPX_DEMO_TEACH_DECK_ID = 'ai-haiku-demo';

/**
 * The exact closing question the demo's simulated student asks, forced verbatim
 * (bypassing the anti-gaming bank selection) so a recorded walkthrough always
 * gets the same question. Sent to the backend as
 * `application_context.demo_closing_question`; only the demo flow sets it.
 */
export const DEMO_SECTION_CLOSING_QUESTION = 'how does call_gpt work exactly';

/**
 * Course the section problem assignment is seeded under. The applicant flow
 * runs on the target course, but the `guessmyanimal-teach` assignment lives
 * under the shared `public` (parent) course, so the IDE loads it from there.
 * TODO: [#TBD] confirm against the running app; if the assignment is seeded
 * per target course instead, drop this override.
 */
export const SECTION_TEACH_PROBLEM_COURSE_ID = 'public';

/**
 * Cap on the applicant's own floor time in seconds. The wording promises a
 * five minute session.
 *
 * @deprecated The section flow now uses a count-up stopwatch
 * ({@link SECTION_QUESTION_TRIGGER_AT_SECONDS} /
 * {@link SECTION_TEACH_AUTO_END_SECONDS}) rather than a countdown. Kept only
 * for any lingering references.
 */
export const SECTION_TEACH_SESSION_TIME_LIMIT_SECONDS = 5 * 60;

/**
 * Elapsed seconds at which the question-asker student (one of the three
 * concurrent students) raises its hand and asks its closing question. Up to
 * this point it stays dormant; the two statement-responder students react to
 * teaching the whole time, independently, and keep doing so after this mark
 * too — triggering the question does not stop teaching. After the applicant
 * answers, the question-asker can run follow-up questions until the
 * applicant clicks "Done answering".
 */
export const SECTION_QUESTION_TRIGGER_AT_SECONDS = 2 * 60;

/**
 * Elapsed seconds at which the session auto-ends — a safety cap only. The
 * applicant is expected to end the session themselves via "Done answering"
 * (after the closing question) or the End button; this stopwatch just stops a
 * session that would otherwise run forever (e.g. a walked-away tab). Set well
 * past {@link SECTION_QUESTION_TRIGGER_AT_SECONDS} so it never cuts off the
 * question-and-answer stretch.
 */
export const SECTION_TEACH_AUTO_END_SECONDS = 15 * 60;

/**
 * Total attempts an applicant may make. The cap is enforced server-side by
 * `beginsectionteachingattempt` (the point a slot is consumed); this constant
 * is only the client-side mirror, used for green-room copy and as the default
 * when the attempt summary carries no `maxAttempts` override.
 *
 * MUST equal `DEFAULT_MAX_SECTION_TEACHING_ATTEMPTS` in the functions package
 * (and `SECTION_MAX_ATTEMPTS` in `seed-cipx-sl-emulator.ts`) — the three are
 * kept in sync by hand. A client value above the server's tells applicants
 * they have attempts the server will refuse.
 */
export const SECTION_TEACH_MAX_ATTEMPTS = 10;

/**
 * Master switch for the section-teaching attempt cap (client mirror). When
 * `false`, the green room never disables "Try again" or shows an exhausted
 * state, so {@link SECTION_TEACH_MAX_ATTEMPTS} above only feeds informational
 * copy. Mirror of `ENFORCE_SECTION_TEACHING_ATTEMPT_CAP` in the functions
 * package's `sl/sectionTeachingAttemptApi.ts` (the authority — it gates the
 * begin callable's server-side rejection); keep the two in sync by hand.
 */
export const SECTION_TEACH_ATTEMPTS_ENFORCED = true;
